import React, { useState, useEffect } from 'react';
import axios from 'axios';
import API_URL from '../config';
import { useUser } from '../context/UserContext';
import Mixpanel from '../utils/mixpanel';
import logger from '../utils/logger';


const ProcessWizard = ({ isOpen, onClose, onComplete }) => {
  const { user } = useUser();
  const [step, setStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [formData, setFormData] = useState({
    affirmations: {
      selectedStatements: [],
      customStatements: []
    },
    selfTalk: {
      selectedStatements: [],
      customStatements: []
    },
    bodyLanguage: {
      selectedStatements: [],
      customStatements: []
    },
    keysToKeepItSimple: {
      selectedStatements: [],
      customStatements: []
    },
    oneWordFocus: {
      selected: ''
    }
  });
  const [content, setContent] = useState({
    affirmations: [],
    selfTalk: [],
    bodyLanguage: [],
    phrases: [],
    oneWordFocus: []
  });
  const [customInput, setCustomInput] = useState('');
  const token = localStorage.getItem('token');

  const steps = [
    { title: 'Introduction', field: 'introduction', description: 'Create your personalized mindset process', instruction: '' },
    { title: 'Affirmations', field: 'affirmations', description: 'Positive statements that reinforce your confidence', instruction: 'Select or write positive, present-tense statements that reflect your goals and values.' },
    { title: 'Self-Talk', field: 'selfTalk', description: 'Phrases to coach yourself during challenges', instruction: 'Choose or create empowering self-talk phrases you can use during training and competition.' },
    { title: 'Body Language', field: 'bodyLanguage', description: 'Physical cues that help embody confidence', instruction: 'Select or describe confident body language and physical cues that work for you.' },
    { title: 'Key Phrases', field: 'keysToKeepItSimple', description: 'Simple reminders to keep you focused', instruction: 'Pick or write short, powerful phrases that help you stay focused and confident.' },
    { title: 'One Word Focus', field: 'oneWordFocus', description: 'A single word to center your mindset', instruction: 'Choose a single word to anchor your mindset in challenging moments.' },
    { title: 'Review & Confirm', field: 'review', description: 'Review and confirm your mindset process', instruction: 'Review your selections and make any final adjustments before confirming.' }
  ];

  const currentStep = steps[step];

  useEffect(() => {
    if (isOpen && user) {
      Mixpanel.trackFeatureUsed('ProcessWizard', 'Opened');
      
      // If we're on a content step, fetch the content
      if (step > 0 && step < steps.length - 1) {
        generateContent(currentStep.field);
      }
    }
  }, [isOpen, step, user]);

  const generateContent = async (stepName) => {
    if (stepName === 'introduction' || stepName === 'review' || loading) return;

    // Map frontend step names to backend type values
    const stepToTypeMap = {
      'affirmations': 'affirmations',
      'selfTalk': 'selfTalk',
      'bodyLanguage': 'bodyLanguage',
      'keysToKeepItSimple': 'phrases',
      'oneWordFocus': 'oneWordFocus'
    };

    setLoading(true);
    setError('');
    
    try {
      const type = stepToTypeMap[stepName];
      if (!type) {
        logger.error('Invalid step name for content generation:', stepName);
        return;
      }

      // Get user data from context or localStorage
      const userData = user || JSON.parse(localStorage.getItem('userData') || '{}');
      
      // Prepare the request payload
      const payload = {
        type: type,
        userData: {
          name: userData?.name || 'User',
          sport: userData?.sport || 'sport',
          position: userData?.sportPosition || 'player',
          gender: userData?.gender || 'unknown',
          yearsPlaying: userData?.yearsPlaying || 0,
          journey: userData?.onboardingResponses?.journey || 'Not specified',
          goals: Array.isArray(userData?.onboardingResponses?.goals) ? 
            userData.onboardingResponses.goals : ['Improve performance'],
          processGoals: Array.isArray(userData?.onboardingResponses?.processGoals) ?
            userData.onboardingResponses.processGoals : ['Practice consistently']
        },
        currentSelections: formData
      };

      logger.debug('Sending request to generate content:', payload);

      const baseUrl = API_URL.endsWith('/') ? API_URL.slice(0, -1) : API_URL;
      const response = await axios.post(
        `${baseUrl}/api/ai/process-suggestions`,
        payload,
        {
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          }
        }
      );

      if (response.data && response.data.suggestions) {
        // Update the content state with the new suggestions
        // Map the backend type back to the frontend field name if needed
        const contentKey = stepName === 'keysToKeepItSimple' ? 'phrases' : stepName;
        
        setContent(prevContent => ({
          ...prevContent,
          [contentKey]: response.data.suggestions
        }));
        
        logger.debug(`Content updated for ${contentKey}:`, response.data.suggestions);
      }
    } catch (error) {
      logger.error('Error generating content:', error);
      setError('Failed to generate suggestions. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleRemoveStatement = (step, listType, index) => {
    setFormData(prev => {
      const updatedStep = { ...prev[step] };
      updatedStep[listType] = [
        ...updatedStep[listType].slice(0, index),
        ...updatedStep[listType].slice(index + 1)
      ];
      return { ...prev, [step]: updatedStep };
    });
  };

  const handleSelectOneWord = (word) => {
    setFormData(prev => ({
      ...prev,
      oneWordFocus: {
        ...prev.oneWordFocus,
        selected: word
      }
    }));
  };

  const handleNext = () => {
    if (step < steps.length - 1) {
      setStep(step + 1);
      
      Mixpanel.trackFeatureAction('ProcessWizard', 'Next Step', {
        fromStep: currentStep.title,
        toStep: steps[step + 1].title,
        timestamp: new Date().toISOString()
      });
      
      // If we're moving to a content step, fetch the content
      if (step + 1 > 0 && step + 1 < steps.length - 1) {
        const nextStepName = steps[step + 1].field;
        generateContent(nextStepName);
      }
    }
  };

  const handleBack = () => {
    if (step > 0) {
      setStep(step - 1);
      
      Mixpanel.trackFeatureAction('ProcessWizard', 'Previous Step', {
        fromStep: currentStep.title,
        toStep: steps[step - 1].title,
        timestamp: new Date().toISOString()
      });
    }
  };

  const handleComplete = async () => {
    setLoading(true);
    setError('');
    
    try {
      // Format the data for saving
      const formattedData = {
        affirmations: [
          ...formData.affirmations.selectedStatements.map(s => s.text),
          ...formData.affirmations.customStatements.map(s => s.text)
        ].filter(text => text && text.trim() !== ''),
        
        selfTalk: [
          ...formData.selfTalk.selectedStatements.map(s => s.text),
          ...formData.selfTalk.customStatements.map(s => s.text)
        ].filter(text => text && text.trim() !== ''),
        
        bodyLanguage: [
          ...formData.bodyLanguage.selectedStatements.map(s => s.text),
          ...formData.bodyLanguage.customStatements.map(s => s.text)
        ].filter(text => text && text.trim() !== ''),
        
        keysToKeepItSimple: [
          ...formData.keysToKeepItSimple.selectedStatements.map(s => s.text),
          ...formData.keysToKeepItSimple.customStatements.map(s => s.text)
        ].filter(text => text && text.trim() !== ''),
        
        oneWordFocus: formData.oneWordFocus.selected
      };
      
      // Save to local storage first as a backup
      localStorage.setItem('mindsetData', JSON.stringify(formattedData));
      
      // Save to backend
      const baseUrl = API_URL.endsWith('/') ? API_URL.slice(0, -1) : API_URL;
      await axios.put(
        `${baseUrl}/api/mindset`,
        formattedData,
        {
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          }
        }
      );
      
      // Track completion
      Mixpanel.trackFeatureAction('ProcessWizard', 'Completed', {
        timestamp: new Date().toISOString()
      });
      
      // Call the onComplete callback
      if (onComplete) {
        onComplete(formattedData);
      }
      
      // Close the wizard
      onClose();
    } catch (error) {
      logger.error('Error saving mindset data:', error);
      setError('Failed to save your mindset process. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const renderIntroduction = () => {
    return (
      <div className="space-y-6">
        <div className="bg-blue-50 p-6 rounded-lg border border-blue-100">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
            <div className="bg-white p-4 rounded-lg shadow-sm">
              <div className="flex items-center mb-2">
                <h4 className="font-bold">Affirmations</h4>
              </div>
              <p className="text-sm">Positive statements that reinforce your confidence and abilities.</p>
            </div>
            
            <div className="bg-white p-4 rounded-lg shadow-sm">
              <div className="flex items-center mb-2">
                <h4 className="font-bold">Self-Talk</h4>
              </div>
              <p className="text-sm">Phrases you can use to coach yourself during challenges.</p>
            </div>
            
            <div className="bg-white p-4 rounded-lg shadow-sm">
              <div className="flex items-center mb-2">
                <h4 className="font-bold">Body Language</h4>
              </div>
              <p className="text-sm">Physical cues that help you embody confidence and focus.</p>
            </div>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
            <div className="bg-white p-4 rounded-lg shadow-sm">
              <div className="flex items-center mb-2">
                <h4 className="font-bold">Key Phrases</h4>
              </div>
              <p className="text-sm">Short, powerful phrases that help you stay focused and confident.</p>
            </div>
            
            <div className="bg-white p-4 rounded-lg shadow-sm">
              <div className="flex items-center mb-2">
                <h4 className="font-bold">One Word Focus</h4>
              </div>
              <p className="text-sm">A single word to instantly center your mindset.</p>
            </div>
          </div>
          
          <h4 className="font-bold mb-2">Why You Need a Mindset Process:</h4>
          <ul className="list-disc list-inside space-y-2">
            <li>Provides consistent mental tools for peak performance</li>
            <li>Builds confidence through positive reinforcement</li>        
            <li>Helps manage pressure and challenging situations</li>
            <li>Creates mental anchors you can rely on during competition</li>
          </ul>
          
          <p className="text-sm italic">
            This wizard will guide you through creating each element of your mindset process, with AI-powered suggestions to help you along the way.
          </p>
        </div>
      </div>
    );
  };

  const renderStatementSection = (section, title, description) => {
    const currentFormData = formData[section];
    // Use the correct content key based on the section
    const contentKey = section === 'keysToKeepItSimple' ? 'phrases' : section;
    const suggestions = content[contentKey] || [];
    
    const handleAddCustom = () => {
      if (!customInput.trim()) return;
      
      setFormData(prev => {
        const updatedSection = { ...prev[section] };
        updatedSection.customStatements = [
          ...updatedSection.customStatements,
          { id: `custom-${Date.now()}`, text: customInput.trim() }
        ];
        return { ...prev, [section]: updatedSection };
      });
      
      setCustomInput('');
    };
    
    const handleToggleSuggestion = (suggestion) => {
      setFormData(prev => {
        const updatedSection = { ...prev[section] };
        const existingIndex = updatedSection.selectedStatements.findIndex(s => s.text === suggestion);
        
        if (existingIndex >= 0) {
          // Remove if already selected
          updatedSection.selectedStatements = updatedSection.selectedStatements.filter((_, i) => i !== existingIndex);
        } else {
          // Add if not selected
          updatedSection.selectedStatements = [
            ...updatedSection.selectedStatements,
            { id: `suggestion-${Date.now()}`, text: suggestion }
          ];
        }
        
        return { ...prev, [section]: updatedSection };
      });
    };
    
    return (
      <div className="space-y-6">
        {/* AI Suggestions with Checkboxes */}
        <div className="space-y-4">
          <h4 className="font-medium">{title} Options</h4>
          
          {loading ? (
            <div className="flex justify-center py-8">
              <div className="flex flex-col items-center gap-2">
                <span className="loading loading-spinner loading-md"></span>
                <p className="text-sm text-gray-600">Generating options...</p>
              </div>
            </div>
          ) : suggestions && suggestions.length > 0 ? (
            <div className="space-y-2">
              {suggestions.map((suggestion, index) => {
                const isSelected = currentFormData.selectedStatements.some(s => s.text === suggestion);
                return (
                  <div 
                    key={index}
                    className={`flex items-start gap-3 p-3 rounded-lg border transition-colors ${
                      isSelected ? 'border-blue-500 bg-blue-50' : 'border-gray-200 hover:border-blue-300'
                    }`}
                  >
                    <div className="flex-shrink-0 pt-0.5">
                      <label className="cursor-pointer flex">
                        <input
                          type="checkbox"
                          className="checkbox checkbox-primary"
                          checked={isSelected}
                          onChange={() => handleToggleSuggestion(suggestion)}
                        />
                      </label>
                    </div>
                    <div className="flex-1">
                      <label className="cursor-pointer" onClick={() => handleToggleSuggestion(suggestion)}>
                        <span className="text-sm">{suggestion}</span>
                      </label>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="text-center py-4 bg-gray-100 rounded-lg">
              <p className="text-sm text-gray-600">No suggestions available</p>
            </div>
          )}
          
          <GenerateButton />
        </div>
        
        {/* Custom Statements Input */}
        <div className="space-y-4">
          <h4 className="font-medium">Add Your Own {title}</h4>
          
          {currentFormData.customStatements.length > 0 && (
            <div className="space-y-2">
              {currentFormData.customStatements.map((statement, index) => (
                <div key={statement.id} className="flex items-start gap-2 p-3 bg-green-50 rounded-lg">
                  <div className="flex-1">
                    <p className="text-sm">{statement.text}</p>
                  </div>
                  <button
                    onClick={() => handleRemoveStatement(section, 'customStatements', index)}
                    className="btn btn-ghost btn-xs btn-square text-error hover:bg-error/10"
                    aria-label="Remove statement"
                  >
                    Remove
                  </button>
                </div>
              ))}
            </div>
          )}
          
          <div className="flex flex-col gap-2">
            <div className="flex gap-2">
              <textarea
                value={customInput}
                onChange={(e) => setCustomInput(e.target.value)}
                className="textarea textarea-bordered flex-1 min-h-[5rem] text-sm resize-none"
                placeholder={`Enter your own ${title.toLowerCase()}...`}
              />
            </div>
            
            <button
              onClick={handleAddCustom}
              disabled={!customInput.trim()}
              className="btn btn-primary btn-sm w-full"
            >
              Add Custom {title}
            </button>
          </div>
        </div>
      </div>
    );
  };

  const renderOneWordFocus = () => {
    const suggestions = content.oneWordFocus || [];
    const selectedWord = formData.oneWordFocus.selected;
    
    const commonWords = [
      "Focus", "Breathe", "Present", "Strong", "Power",
      "Calm", "Flow", "Trust", "Believe", "Commit"
    ];
    
    // Combine AI suggestions with common words, removing duplicates
    const allWords = [...new Set([...suggestions, ...commonWords])];
    
    return (
      <div className="space-y-6">
        {/* Word Selection */}
        <div className="space-y-4">
          <h4 className="font-medium">Choose Your Focus Word</h4>
          
          {loading ? (
            <div className="flex justify-center py-8">
              <div className="flex flex-col items-center gap-2">
                <span className="loading loading-spinner loading-md"></span>
                <p className="text-sm text-gray-600">Generating suggestions...</p>
              </div>
            </div>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
              {allWords.map((word, index) => (
                <button
                  key={index}
                  onClick={() => handleSelectOneWord(word)}
                  className={`btn ${
                    selectedWord === word
                      ? 'btn-primary'
                      : 'btn-outline btn-neutral'
                  } h-auto py-3 normal-case font-medium`}
                >
                  {word}
                </button>
              ))}
            </div>
          )}
          
          <GenerateButton />
        </div>
        
        {/* Custom Word Input */}
        <div className="space-y-4">
          <h4 className="font-medium">Or Enter Your Own Word</h4>
          
          <div className="flex items-center gap-2">
            <input
              type="text"
              value={customInput}
              onChange={(e) => setCustomInput(e.target.value)}
              className="input input-bordered flex-1"
              placeholder="Enter your own focus word..."
              maxLength={15}
            />
            
            <button
              onClick={() => {
                if (customInput.trim()) {
                  handleSelectOneWord(customInput.trim());
                  setCustomInput('');
                }
              }}
              disabled={!customInput.trim()}
              className="btn btn-primary"
            >
              Select
            </button>
          </div>
        </div>
        
        {/* Selected Word Display */}
        {selectedWord && (
          <div className="bg-blue-50 p-6 rounded-lg border border-blue-100 text-center">
            <h3 className="text-lg font-bold mb-2">Your Focus Word</h3>
            <div className="text-4xl font-extrabold tracking-wider text-blue-600">{selectedWord}</div>
          </div>
        )}
      </div>
    );
  };

  const renderReview = () => {
    // Helper function to render a review section
    const renderReviewSection = (title, data) => {
      
      if (!data || (Array.isArray(data) && data.length === 0)) {
        return null;
      }
      
      return (
        <div className="card bg-base-100 shadow-sm">
          <div className="card-body">
            <h3 className="card-title text-lg font-bold mb-2">{title}</h3>
            
            {Array.isArray(data) ? (
              <ul className="list-disc list-inside space-y-2">
                {data.map((item, index) => (
                  <li key={index} className="bg-base-200 p-3 rounded-lg">
                    {item}
                  </li>
                ))}
              </ul>
            ) : (
              <div className="bg-base-200 p-4 rounded-lg text-center">
                <p className="text-xl font-bold">{data}</p>
              </div>
            )}
          </div>
        </div>
      );
    };
    
    // Prepare data for review
    const affirmations = [
      ...formData.affirmations.selectedStatements.map(s => s.text),
      ...formData.affirmations.customStatements.map(s => s.text)
    ].filter(text => text && text.trim() !== '');
    
    const selfTalk = [
      ...formData.selfTalk.selectedStatements.map(s => s.text),
      ...formData.selfTalk.customStatements.map(s => s.text)
    ].filter(text => text && text.trim() !== '');
    
    const bodyLanguage = [
      ...formData.bodyLanguage.selectedStatements.map(s => s.text),
      ...formData.bodyLanguage.customStatements.map(s => s.text)
    ].filter(text => text && text.trim() !== '');
    
    const phrases = [
      ...formData.keysToKeepItSimple.selectedStatements.map(s => s.text),
      ...formData.keysToKeepItSimple.customStatements.map(s => s.text)
    ].filter(text => text && text.trim() !== '');
    
    const oneWordFocus = formData.oneWordFocus.selected;
    
    return (
      <div className="space-y-6">
        {affirmations.length > 0 && renderReviewSection("Affirmations & Beliefs", affirmations)}
        {selfTalk.length > 0 && renderReviewSection("Self-Talk", selfTalk)}
        {bodyLanguage.length > 0 && renderReviewSection("Body Language", bodyLanguage)}
        {phrases.length > 0 && renderReviewSection("Key Phrases", phrases)}
        {oneWordFocus && renderReviewSection("One Word Focus", oneWordFocus)}
        
        {affirmations.length === 0 && selfTalk.length === 0 && bodyLanguage.length === 0 && phrases.length === 0 && !oneWordFocus && (
          <p className="text-gray-500 italic text-center">No mindset elements defined</p>
        )}
      </div>
    );
  };

  const renderInputField = () => {
    const field = currentStep.field;
    
    // For intro and review steps, show the content
    if (field === 'introduction') {
      return renderIntroduction();
    }
    
    if (field === 'review') {
      return renderReview();
    }
    
    if (field === 'oneWordFocus') {
      return renderOneWordFocus();
    }
    
    // For statement sections
    const sectionMap = {
      'affirmations': { title: 'Affirmations' },
      'selfTalk': { title: 'Self-Talk' },
      'bodyLanguage': { title: 'Body Language' },
      'keysToKeepItSimple': { title: 'Key Phrases' }
    };
    
    if (field in sectionMap) {
      return renderStatementSection(
        field,
        sectionMap[field].title,
        currentStep.description
      );
    }
    
    return null;
  };

  // Create a reusable AI Generate button component
  const GenerateButton = () => {
    return (
      <div className="flex justify-end">
        <button
          onClick={() => generateContent(currentStep.field)}
          disabled={loading}
          className="btn btn-primary btn-xs"
        >
          {loading ? "Generating..." : "Generate"}
        </button>
      </div>
    );
  };

  if (!isOpen) return null;

  return (
    <div className={`fixed inset-0 z-50 flex items-center justify-center ${isOpen ? 'visible' : 'invisible'}`}>
      <div className="absolute inset-0 bg-black/50" onClick={onClose}></div>
      <div className="relative bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] overflow-auto">
        <div className="sticky top-0 z-10 flex justify-between items-center border-b p-4 bg-white">
          <h2 className="text-xl font-bold text-gray-900 whitespace-nowrap">Mindset Process Wizard</h2>
          <button
            onClick={onClose}
            className="btn btn-sm btn-circle btn-ghost ml-4"
            aria-label="Close"
          >
            Close
          </button>
        </div>
        
        <div className="p-4 md:p-6">
          {/* Simple progress bar instead of circles */}
          <div className="mb-6 md:mb-8">
            <div className="relative h-2 bg-gray-200 rounded-full overflow-hidden">
              <div 
                className="absolute top-0 left-0 h-full bg-blue-500 transition-all duration-300"
                style={{ width: `${(step / (steps.length - 1)) * 100}%` }}
              ></div>
            </div>
          </div>
          
          {/* Step content */}
          <div className="mb-6 md:mb-8">
            {/* Add heading for each step */}
            <div className="mb-6">
              <h2 className="text-2xl font-bold">
                {currentStep.field === 'introduction' && "What Is Your Mindset Process?"}
                {currentStep.field === 'affirmations' && "Your Affirmations & Beliefs"}
                {currentStep.field === 'selfTalk' && "Your Self-Talk"}
                {currentStep.field === 'bodyLanguage' && "Your Body Language"}
                {currentStep.field === 'keysToKeepItSimple' && "Your Key Phrases"}
                {currentStep.field === 'oneWordFocus' && "Your One Word Focus"}
                {currentStep.field === 'review' && "Review Your Mindset Process"}
              </h2>
              <p className="text-sm text-gray-600">
                {currentStep.description}
              </p>
              <p className="text-sm text-gray-600 mt-1">
                {currentStep.instruction}
              </p>
            </div>
            
            {error && (
              <div className="text-center mb-4">
                <p className="text-red-500 font-medium">{error}</p>
              </div>
            )}
            
            {renderInputField()}
          </div>
          
          {/* Navigation buttons */}
          <div className="flex flex-col-reverse md:flex-row justify-between gap-3 mt-6">
            <button
              onClick={handleBack}
              disabled={step === 0}
              className="btn btn-outline btn-neutral w-full md:w-auto border-2 border-blue-500"
            >
              Back
            </button>
            
            {step < steps.length - 1 ? (
              <button
                onClick={handleNext}
                className="btn btn-primary w-full md:w-auto"
              >
                Next
              </button>
            ) : (
              <button
                onClick={handleComplete}
                disabled={loading}
                className="btn btn-primary w-full md:w-auto"
              >
                {loading ? (
                  <>
                    Saving...
                  </>
                ) : (
                  <>
                    Complete
                  </>
                )}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProcessWizard;